import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import ActionsMenu from "@dashboard/files/components/ActionsMenu/ActionsMenu";
import { useGetPolicyByIdQuery } from "@dashboard/policies/api";
import { FileSettingsDialog } from "@dashboard/shared/components/FileSettingsDialog";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { selectGroupDetails, setPaneTab } from "@devices/store";
import { FileInfo } from "@files/types";
import { isApk } from "@files/utils";
import { getFileLevelApkStatus } from "@files/utils/apkStatus";
import { selectGroupById } from "@groups/store/selectors";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useStyles } from "../FileTab.style";
import { getGroupApkTargets, getNoEligibleDevicesTooltipKey } from "../groupApkStatus";
import { useGroupDevices } from "../GroupDevicesContext";
import { installFileOnGroupDevices } from "../installPackage";

import { PickInstallationTypeDialog } from "./PickInstallationTypeDialog";

const installIcon = "fa-solid fa-arrow-right-to-bracket";

type Props = {
    file: FileInfo;
    groupId: number;
    /** Older versions install straight away; only the newest version also offers the group policy. */
    isOlderVersion?: boolean;
};
const ActionsCell = (props: Props) => {
    const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
    const [isInstallationTypeModalOpen, setIsInstallationTypeModalOpen] = React.useState(false);
    const [isInstalling, setIsInstalling] = React.useState(false);

    const { t } = useTranslation(["sidepanels"]);
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const group = useSelector((state) => selectGroupById(state, props.groupId));
    const isApkFile = isApk(props.file.filename);

    const isExpired = useCurrentSubscriptionExpired();

    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility, fileAbility, workspaceAbility } = PermissionService();
    const mayManageTags = fileAbility(props.file).can("manage", "Tag");

    const { devices, installedPackages } = useGroupDevices();
    const targets = React.useMemo(
        () => getGroupApkTargets(props.file, devices, installedPackages),
        [props.file, devices, installedPackages],
    );

    const { data: assignedPolicyResponse, isLoading: isLoadingPolicies } = useGetPolicyByIdQuery(
        group?.policyId && !isExpired ? { id: group.policyId } : skipToken,
    );
    const assignedPolicy = assignedPolicyResponse?.model;

    const canInstallNow = targets.eligible.length > 0;
    // Pinning a version in the policy stays useful even when every device already runs it, but only the
    // newest version offers it — and only when the assigned policy actually loaded. Same value the
    // dialog uses, or the button stays live over a dialog whose every option is disabled.
    const canAddToPolicy =
        !props.isOlderVersion &&
        devices.length > 0 &&
        Boolean(assignedPolicy) &&
        workspaceAbility(workspace ?? null).can("update", "Policy");

    const getInstallButtonProps = () => {
        if (groupAbility(group || null, workspace).cannot("install", "App")) {
            return { tooltip: t("sidepanels:groupPanel.tooltip.installError"), iconClass: installIcon, disabled: true };
        }
        if (isExpired) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.subscriptionExpired"),
                iconClass: installIcon,
                disabled: true,
            };
        }

        switch (getFileLevelApkStatus(props.file)) {
            case "processing":
                return {
                    tooltip: t("sidepanels:groupPanel.tooltip.processing"),
                    iconClass: "fa-solid fa-spinner fa-spin",
                    disabled: true,
                };
            case "analyzerFailed":
                return {
                    tooltip: t("sidepanels:groupPanel.tooltip.analysisFailed"),
                    iconClass: "fa-solid fa-triangle-exclamation",
                    disabled: true,
                };
            case "invalidApk":
                return {
                    tooltip: t("sidepanels:groupPanel.tooltip.invalidPackage"),
                    iconClass: installIcon,
                    disabled: true,
                };
            default:
                break;
        }

        if (!targets.totalDevices) {
            return { tooltip: t("sidepanels:groupPanel.tooltip.noDevices"), iconClass: installIcon, disabled: true };
        }
        if (!canInstallNow) {
            return {
                // Enabled here only because the version can still be pinned in the policy: the button has to say
                // what the click does, not repeat the state that made an immediate install pointless.
                tooltip: canAddToPolicy
                    ? t("sidepanels:groupPanel.tooltip.addToGroupPolicy")
                    : t(getNoEligibleDevicesTooltipKey(targets)),
                iconClass: targets.upToDateCount ? "fa-solid fa-check" : "fa-solid fa-angles-down",
                disabled: !canAddToPolicy,
            };
        }
        if (targets.hasOutdatedInstall) {
            return {
                tooltip: t("sidepanels:groupPanel.tooltip.updateDevices", {
                    count: targets.eligible.length,
                    version: props.file.metadata?.file?.apk?.manifest?.versioncode,
                }),
                iconClass: "fa-solid fa-angles-up",
                disabled: false,
            };
        }

        return {
            tooltip: t("sidepanels:groupPanel.tooltip.installOnDevices", { count: targets.eligible.length }),
            iconClass: installIcon,
            disabled: false,
        };
    };
    const getTagBtnTitle = () => {
        const result = { title: t("sidepanels:groupPanel.tooltip.manageTags") };

        if (isExpired) {
            result.title = t("sidepanels:groupPanel.tooltip.subscriptionExpired");
        }
        if (!mayManageTags) {
            result.title = t("sidepanels:groupPanel.tooltip.manageTagsError");
        }
        return result;
    };

    const installOlderVersion = async () => {
        setIsInstalling(true);
        try {
            const result = await installFileOnGroupDevices({ file: props.file, groupId: props.groupId, targets });

            if (result.reached) {
                showSuccessToast(t("sidepanels:groupPanel.toast.installQueued", { count: result.reached }));
            }
            if (result.failed) {
                showErrorToast(t("sidepanels:groupPanel.toast.installFailed", { count: result.failed }));
            }
            if (result.usedGroupCommand) {
                dispatch(setPaneTab("COMMANDS"));
            }
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setIsInstalling(false);
        }
    };

    const installButtonProps = getInstallButtonProps();

    return (
        <React.Fragment>
            <Box className={classes.buttonBox}>
                {isApkFile && (
                    <SecondaryButton
                        onClick={props.isOlderVersion ? installOlderVersion : () => setIsInstallationTypeModalOpen(true)}
                        tooltipProps={{ title: installButtonProps.tooltip }}
                        disabled={installButtonProps.disabled || isInstalling}
                        loading={isInstalling}
                    >
                        <i className={installButtonProps.iconClass} />
                    </SecondaryButton>
                )}
                <SecondaryButton
                    onClick={() => setIsSettingsModalOpen(true)}
                    tooltipProps={getTagBtnTitle()}
                    disabled={!mayManageTags}
                >
                    <i className="fa-solid fa-tags" />
                </SecondaryButton>
                <ActionsMenu file={props.file} />
            </Box>

            {group ? (
                <PickInstallationTypeDialog
                    file={props.file}
                    group={group}
                    targets={targets}
                    assignedPolicy={assignedPolicy}
                    isLoadingPolicies={isLoadingPolicies}
                    open={isInstallationTypeModalOpen}
                    onClose={() => setIsInstallationTypeModalOpen(false)}
                />
            ) : null}
            <FileSettingsDialog item={props.file} open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
        </React.Fragment>
    );
};

export default ({ file, isOlderVersion }: { file: FileInfo; isOlderVersion?: boolean }) => {
    const details = useSelector(selectGroupDetails);

    if (!details?.selectedId) {
        return null;
    }

    return <ActionsCell {...{ file, isOlderVersion, groupId: details.selectedId }} />;
};
